import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';

import {TemperatureConverterPipe, TemperatureUnit} from '../../../../shared';
import {EventService, IDeviceMinAvg, IOrgMinAvg} from '../../../shared';
import {IAwairTimelineSet, ITimeLine, ITimePoint} from '../';

import * as fromRoot from '../../../../reducers';

import {combineLatest, Observable} from 'rxjs';

import {AirComponentType} from '../../../../afb-api/enums/air-component-type.enum';
import {map, withLatestFrom} from 'rxjs/internal/operators';

@Injectable()
export class TimelineService {

  constructor(private store: Store<fromRoot.State>,
              private eventService: EventService,
              // Should convert temperature unit here, not in the template, since graph is dependent on these values
              private tempConverter: TemperatureConverterPipe) {
  }

  getTimelineInputContainer(tempUnit: TemperatureUnit): Observable<any> {
    const airDataEntities$: Observable<{ [deviceUuid: string]: IDeviceMinAvg[] }> = this.store.pipe(
      select(fromRoot.getAirDataEntities)
    );
    const timeRange$: Observable<{ fromDate: Date, toDate: Date }> = this.store.pipe(
      select(fromRoot.getAirDataTimeRange)
    );

    return airDataEntities$.pipe(
      withLatestFrom(timeRange$),
      map(([entities, timeRange]: [{ [deviceUuid: string]: IDeviceMinAvg[] }, { fromDate: Date, toDate: Date }]) => {
        const deviceAirDataList = {};

        Object.keys(entities).map((key: string) => {
          deviceAirDataList[key] = this.getMinAvgTimelineSet(entities[key], timeRange.fromDate, timeRange.toDate, 15, tempUnit);
        });

        return deviceAirDataList;
      }),
      map((deviceAirDataList: { [deviceUuid: string]: IAwairTimelineSet }) => {
        const timelineInputContainer = {
          'score': [],
          'temp': [],
          'humid': [],
          'co2': [],
          'voc': [],
          'dust': [],
          'pm25': []
        };
        Object.keys(deviceAirDataList).map((deviceUuid: string) => {
          timelineInputContainer['score'].push(deviceAirDataList[deviceUuid]['score']);
          timelineInputContainer['temp'].push(deviceAirDataList[deviceUuid]['temp']);
          timelineInputContainer['humid'].push(deviceAirDataList[deviceUuid]['humid']);
          timelineInputContainer['co2'].push(deviceAirDataList[deviceUuid]['co2']);
          timelineInputContainer['voc'].push(deviceAirDataList[deviceUuid]['voc']);
          timelineInputContainer['dust'].push(deviceAirDataList[deviceUuid]['dust']);
          timelineInputContainer['pm25'].push(deviceAirDataList[deviceUuid]['pm25']);
        });

        return timelineInputContainer;
      })
    );
  }

  getAvgTimelineInputContainer(tempUnit: TemperatureUnit): Observable<IAwairTimelineSet> {
    const avgAirData$: Observable<IOrgMinAvg[]> = this.store.pipe(
      select(fromRoot.getAvgAirData)
    );
    const timeRange$: Observable<{ fromDate: Date, toDate: Date }> = this.store.pipe(
      select(fromRoot.getAvgAirDataTimeRange)
    );

    return avgAirData$.pipe(
      withLatestFrom(timeRange$),
      map(([minAvgList, timeRange]: [IOrgMinAvg[], { fromDate: Date, toDate: Date }]) => {
        return this.getMinAvgTimelineSet(minAvgList, timeRange.fromDate, timeRange.toDate, 15, tempUnit)
      })
    );
  }

  isTimelineLoading(): Observable<boolean> {
    const airDataLoading$: Observable<boolean> = this.store.pipe(
      select(fromRoot.getAirDataLoadingStatus)
    );
    const avgAirDataLoading$: Observable<boolean> = this.store.pipe(
      select(fromRoot.getAvgAirDataLoadingStatus)
    );

    return combineLatest(airDataLoading$, avgAirDataLoading$, (airDataLoading: boolean, avgAirDataLoading: boolean) => {
      return (airDataLoading || avgAirDataLoading);
    });
  }

  getMinAvgTimelineSet(
    minAvgList: IDeviceMinAvg[] | IOrgMinAvg[], fromDate: Date, toDate: Date, interval: number, tempUnit: TemperatureUnit
  ): IAwairTimelineSet {
    const awairTimelineSet: IAwairTimelineSet = {
      score: [],
      temp: [],
      humid: [],
      co2: [],
      voc: [],
      dust: [],
      pm25: []
    };

    for (let i = 0; i < minAvgList.length; i++) {
      // Comparing each component to null since 0 can get into value
      // minAvgList[i].{component} : null also turns 0 to null
      if (minAvgList[i] != null) {
        awairTimelineSet.score.push({
          x: minAvgList[i].timestamp,
          y: minAvgList[i].score != null ? minAvgList[i].score : null
        });
        awairTimelineSet.temp.push({
          x: minAvgList[i].timestamp,
          y: this.tempConverter.transform(
            minAvgList[i].sensors.temp, AirComponentType.Temperature, tempUnit) != null ?
            this.tempConverter.transform(minAvgList[i].sensors.temp, AirComponentType.Temperature, tempUnit) : null
        });
        awairTimelineSet.humid.push({
          x: minAvgList[i].timestamp,
          y: minAvgList[i].sensors.humid != null ? minAvgList[i].sensors.humid : null
        });
        awairTimelineSet.co2.push({
          x: minAvgList[i].timestamp,
          y: minAvgList[i].sensors.co2 != null ? minAvgList[i].sensors.co2 : null
        });
        awairTimelineSet.voc.push({
          x: minAvgList[i].timestamp,
          y: minAvgList[i].sensors.voc != null ? minAvgList[i].sensors.voc : null
        });
        // Null gets in for glow
        awairTimelineSet.dust.push({
          x: minAvgList[i].timestamp,
          y: minAvgList[i].sensors.dust != null ? minAvgList[i].sensors.dust : null
        });
        // Null gets in for glow
        awairTimelineSet.pm25.push({
          x: minAvgList[i].timestamp,
          y: minAvgList[i].sensors.pm25 != null ? minAvgList[i].sensors.pm25 : null
        });
      }
    }

    return {
      score: this.fillTimelineGap(fromDate, toDate, awairTimelineSet.score, interval),
      temp: this.fillTimelineGap(fromDate, toDate, awairTimelineSet.temp, interval),
      humid: this.fillTimelineGap(fromDate, toDate, awairTimelineSet.humid, interval),
      co2: this.fillTimelineGap(fromDate, toDate, awairTimelineSet.co2, interval),
      voc: this.fillTimelineGap(fromDate, toDate, awairTimelineSet.voc, interval),
      dust: this.fillTimelineGap(fromDate, toDate, awairTimelineSet.dust, interval),
      pm25: this.fillTimelineGap(fromDate, toDate, awairTimelineSet.pm25, interval)
    };
  }

  fillTimelineGap(fromDate: Date, toDate: Date, timeline: ITimeLine, avgInterval: number): ITimeLine {
    const newTimeline: ITimeLine = [];

    const startTime: Date = this.getAvgStartTime(fromDate, avgInterval);
    // Counter to scan fromDate ~ toDate
    const timeCtr: Date = new Date(startTime);

    let i: number = 0;
    while (timeCtr.getTime() < toDate.getTime()) {
      if (i > timeline.length - 1) {
        // Add null timeline point if there's empty data between timeline's last index time and toDate
        newTimeline.push(this.getNullTimelinePoint(timeCtr.toISOString()));
        timeCtr.setMinutes(timeCtr.getMinutes() + avgInterval);

      } else if (timeCtr.getTime() < new Date(timeline[i].x).getTime()) {
        // Add null timeline point if there's empty data between timeline's first index time and fromDate
        // or empty data in the middle of timeline
        newTimeline.push(this.getNullTimelinePoint(timeCtr.toISOString()));
        timeCtr.setMinutes(timeCtr.getMinutes() + avgInterval);

      } else /*if (timeCtr.getTime() === timeline[i].time.getTime())*/ {
        // Add existing timeline data if such exists within time range
        newTimeline.push(timeline[i]);
        timeCtr.setMinutes(timeCtr.getMinutes() + avgInterval);
        i++;
      }
    }

    return newTimeline;
  }

  getTimelineMaxProp(timelineList: Array<ITimeLine>, propType: string): number {
    const maxList: number[] = [];

    // Indicates a sensor timeline of a device
    timelineList.forEach((timeline: ITimeLine) => {
      if (timeline != null) {
        maxList.push(
          Math.max.apply(Math, timeline.map((timePoint: ITimePoint) => timePoint[propType]))
        );
      }
    });

    return Math.max.apply(Math, maxList);
  }

  getTimelineMinProp(timelineList: Array<ITimeLine>, propType: string): number {
    const minList: number[] = [];

    // Indicates a sensor timeline of a device
    timelineList.forEach((timeline: ITimeLine) => {
      // Checks and filters null value (converting to Infinity)
      if (timeline != null) {
        minList.push(
          Math.min.apply(Math, timeline.map((timePoint: ITimePoint) => timePoint[propType] === null ? Infinity : timePoint[propType]))
        );
      }
    });

    return Math.min.apply(Math, minList);
  }

  private getNullTimelinePoint(x: string): ITimePoint {
    const timePoint: ITimePoint = {
      x: x,
      y: null
    };

    return timePoint;
  }

  private getAvgStartTime(fromDate: Date, avgInterval: number): Date {
    const startTime: Date = new Date(fromDate);
    let newMin: number = 0;
    const min = fromDate.getMinutes();

    while (min > newMin) {
      newMin += avgInterval;
    }

    if (newMin === 60) {
      startTime.setHours(fromDate.getHours() + 1);
      startTime.setMinutes(0);
      startTime.setSeconds(0);

    } else {
      startTime.setMinutes(newMin);
      startTime.setSeconds(0);
    }

    return startTime;
  }
}
